import type { AxiosError } from 'axios';
import { workspace } from '../../shared/api';
import { logger } from '../../helpers/logger';
import { fetchDoctorRqes } from '../../shared/bannerApi';
import { findSpecialtyByRqes } from '../../helpers/rqeSpecialties';
import {
  FreeSampleDeliveryAddress,
  FreeSampleMedicine,
  FreeSampleProgram,
  FreeSampleRequestOutput,
  FreeSampleRequestPayload,
} from '../../interfaces/freeSample';

export interface FreeSampleEligibilityQuery {
  crmNumber: string;
  crmState: string;
  cpf: string;
}

// Amostras só são entregues no local de atendimento (regra de negócio já validada com produto) —
// não existe input de "tipo de endereço" em nenhuma tela, então fixamos esse valor ao montar o
// payload de /request.
const ADDRESS_TYPE = 'Consultório';

// As chamadas passam pelo proxy do workspace (nx-workspace, VenturesFreeSamplesController), como o
// PSP e as campanhas — o front não fala direto com o ventures-api. Diferente do PSP, o proxy mantém
// o prefixo `/product` nos paths.
export const FreeSampleService = {
  // Descobre a campanha ativa antes de chamar eligibility/request — GET /product/free-samples não
  // tem filtro, retorna todos os programas (ativos e inativos), então filtramos aqui. Se houver
  // mais de um ativo, pega o primeiro — piloto atual só tem uma campanha por vez.
  async getActiveProgram(): Promise<FreeSampleProgram | null> {
    try {
      const { data } = await workspace.get('/product/free-samples');
      const programs: Array<{
        id: number;
        active: boolean;
        optoutUrl?: string;
        partnerProgramUrl?: string;
        pharmaceuticalPageUrl?: string;
        pharmaceuticalPhone?: string;
      }> = data ?? [];

      const active = programs.find((program) => program.active);
      if (!active) return null;

      return {
        id: active.id,
        optoutUrl: active.optoutUrl,
        partnerProgramUrl: active.partnerProgramUrl,
        pharmaceuticalPageUrl: active.pharmaceuticalPageUrl,
        pharmaceuticalPhone: active.pharmaceuticalPhone,
      };
    } catch (error) {
      logger.error('[FreeSample] Erro ao buscar programa ativo:', { error });
      throw error;
    }
  },

  async getCatalog(freeSampleId: number, query: FreeSampleEligibilityQuery): Promise<FreeSampleMedicine[]> {
    try {
      const { data } = await workspace.post('/product/free-samples/eligibility', {
        freeSampleId,
        crmNumber: query.crmNumber,
        crmState: query.crmState,
        cpf: query.cpf,
      });

      return data?.medicines ?? [];
    } catch (error) {
      logger.error('[FreeSample] Erro ao consultar elegibilidade:', { error });
      throw error;
    }
  },

  async getSpecialtyFromRqes(prescriberId: number): Promise<string> {
    try {
      const response = await fetchDoctorRqes(prescriberId);
      const rqes: Array<{ idRqe?: string | null }> = Array.isArray(response?.data) ? response.data : [];

      return findSpecialtyByRqes(rqes);
    } catch (error) {
      logger.error('[FreeSample] Erro ao buscar RQEs do médico:', { error });
      return '';
    }
  },

  async submitRequest(payload: FreeSampleRequestPayload): Promise<FreeSampleRequestOutput> {
    try {
      const { data } = await workspace.post('/product/free-samples/request', {
        freeSampleId: payload.freeSampleId,
        idempotencyKey: payload.idempotencyKey,
        items: payload.items,
        cpf: payload.prescriber.cpf,
        name: payload.prescriber.name,
        email: payload.prescriber.email,
        ...(payload.prescriber.phone ? { phone: payload.prescriber.phone } : {}),
        ...(payload.prescriber.birthdate ? { birthdate: payload.prescriber.birthdate } : {}),
        crmNumber: payload.prescriber.crmNumber,
        crmState: payload.prescriber.crmState,
        specialty: payload.prescriber.specialty,
        addressType: ADDRESS_TYPE,
        zipCode: payload.deliveryAddress.postalCode,
        street: payload.deliveryAddress.address,
        number: payload.deliveryAddress.withoutNumber ? 's/n' : payload.deliveryAddress.number,
        ...(payload.deliveryAddress.complement ? { complement: payload.deliveryAddress.complement } : {}),
        neighborhood: payload.deliveryAddress.neighborhood,
        city: payload.deliveryAddress.city,
        // `communicationsConsent` não tem campo correspondente no backend ainda — não é enviado
        // (ver comentário em FreeSampleRequestPayload).
        optIn: payload.shippingConsent,
      });

      if (!Array.isArray(data?.results)) {
        throw new Error(`Resposta inesperada de POST /product/free-samples/request: "results" ausente ou inválido`);
      }

      return data;
    } catch (error) {
      // Quando todos os itens são recusados, o backend responde com o status do erro (ex.: 409 no
      // limite individual) e o mesmo corpo `results[]` do sucesso — é resultado, não falha de envio.
      const results = (error as AxiosError<Partial<FreeSampleRequestOutput>>)?.response?.data?.results;
      if (Array.isArray(results)) {
        return { results };
      }

      logger.error('[FreeSample] Erro ao enviar solicitação de amostra:', { error });
      throw error;
    }
  },
};

export const buildAddressSummary = (address: FreeSampleDeliveryAddress): string => {
  const numberPart = address.withoutNumber ? 's/n' : address.number;
  const complementPart = address.complement ? ` · ${address.complement}` : '';

  return `${address.address}, ${numberPart}${complementPart} · ${address.neighborhood} · ${address.city}/${address.state}`;
};
